import { useState } from 'react'
import { ProjectPlanResponse, SuitabilityPreview, type ProjectPlan, type SuitabilityFacts, type SuitabilityAssessment } from '@nimblybase/contracts'
import { type Api } from '../lib/api.js'
import { AT_CREATION } from '../lib/boundaries.js'
import { ErrorBox } from './ui.js'

type Recipe = '' | 'static-d1' | 'existing' | 'postgres' | 'process'
export function creationFacts(recipe: Recipe): SuitabilityFacts {
  return {
    schemaVersion: 1, projectKind: recipe === 'existing' ? 'existing' : 'new', intent: 'full-app',
    frontend: recipe === 'static-d1' ? 'static' : 'unknown',
    server: recipe === 'process' ? 'process' : recipe === 'static-d1' ? 'worker' : 'unknown',
    database: recipe === 'postgres' ? 'postgres' : recipe === 'static-d1' ? 'd1' : 'unknown',
    requirements: recipe === 'postgres' ? ['postgres-wire-protocol'] : [],
    requiredServices: [], inspection: recipe === 'static-d1' ? 'complete' : 'not-inspected',
    evidence: recipe === 'static-d1' ? ['User explicitly selected the declared static-d1 new-app recipe; no existing source or build was inspected.'] : ['Dashboard cannot inspect a local repository.'],
    unknowns: recipe === 'static-d1' ? [] : ['Use the connected AI tool or CLI to inspect repository requirements before provisioning.'],
  }
}

export function ProjectCreation({ api, onCreated, onBusy }: { api: Api; onCreated(): void; onBusy(busy: boolean): void }) {
  const [name, setName] = useState('')
  const [recipe, setRecipe] = useState<Recipe>('')
  const [assessment, setAssessment] = useState<SuitabilityAssessment | null>(null)
  const [plan, setPlan] = useState<ProjectPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const lock = (value: boolean) => { setBusy(value); onBusy(value) }
  const changed = () => { setAssessment(null); setPlan(null); setError(null) }
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'The request could not be completed.')

  async function review(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !name.trim() || !recipe) return
    lock(true); setError(null); setPlan(null)
    try {
      const facts = creationFacts(recipe)
      const preview = SuitabilityPreview.parse(await api.assessProject(facts))
      setAssessment(preview.assessment)
      if (preview.assessment.decision !== 'supported') return
      const recorded = ProjectPlanResponse.parse(await api.createProjectPlan({ name: name.trim(), facts }))
      setPlan(recorded.plan)
    } catch (err) { fail(err) }
    finally { lock(false) }
  }
  async function create() {
    if (busy || !plan) return
    lock(true); setError(null)
    try {
      const current = ProjectPlanResponse.parse(await api.getProjectPlan(plan.id)).plan
      if (current.state !== 'eligible') throw new Error(`This plan is ${current.state}. Review the requirements again.`)
      if (current.factsFingerprint !== plan.factsFingerprint || current.target.name !== name.trim()) throw new Error('The plan changed; review the requirements again.')
      await api.createProject({ name: name.trim(), databaseTier: 'd1', planId: plan.id, factsFingerprint: plan.factsFingerprint })
      onCreated()
    } catch (err) { fail(err) }
    finally { lock(false) }
  }
  return (
    <form className="card" onSubmit={review} style={{ padding: 16, marginBottom: 20 }}>
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="field">
        <label htmlFor="p-name">Project name</label>
        <input id="p-name" value={name} disabled={busy} onChange={e => { setName(e.target.value); changed() }} placeholder="My todo app" maxLength={100} required />
      </div>
      <div className="field">
        <label htmlFor="p-recipe">What are you building?</label>
        <select id="p-recipe" value={recipe} disabled={busy} onChange={e => { setRecipe(e.target.value as Recipe); changed() }} required>
          <option value="">Choose your application requirements</option>
          <option value="static-d1">A new static web app with a light database and edge functions</option>
          <option value="existing">An existing codebase — inspect it with my connected AI tool</option>
          <option value="postgres">An app that requires managed Postgres</option>
          <option value="process">An app that requires a conventional server or container</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="p-tier">Database in this release</label>
        <select id="p-tier" defaultValue="d1" disabled>
          <option value="d1">D1 — light</option>
          <option value="neon" disabled>Neon — not available yet</option>
        </select>
      </div>
      <p className="sub small">Existing code needs local inspection through your AI tool or <code>nimbly assess</code>. A language name alone does not establish compatibility.</p>
      <button className="btn" type="submit" disabled={busy || !name.trim() || !recipe}>{busy ? 'Checking…' : 'Review requirements'}</button>
      {assessment && (
        <section aria-label="Suitability result" style={{ marginTop: 16 }}>
          <strong>{assessment.decision === 'supported' ? 'Declared requirements supported' : assessment.decision === 'unsupported' ? 'Requirements unavailable in V1' : 'More information needed'}</strong>
          {assessment.reasons.map((reason, i) => <p key={`${reason.code}-${i}`}>{reason.message}</p>)}
        </section>
      )}
      {plan && (
        <section aria-label="Creation plan" style={{ marginTop: 16 }}>
          <p>Plan for <strong>{plan.target.name}</strong> · expires {new Date(plan.expiresAt).toLocaleString()}</p>
          <p className="sub small">{plan.note}</p>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void create()}>Create project</button>
        </section>
      )}
      <p className="sub small" style={{ marginTop: 10 }}>{AT_CREATION}</p>
    </form>
  )
}
